import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { FiSave, FiRefreshCw, FiInfo, FiLock } from 'react-icons/fi'
import './Settings.css'

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:3001/api'

const Settings = () => {
  const { user, logout } = useAuth()
  const [saved, setSaved] = useState(false)
  const [pwData, setPwData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })
  const [pwError, setPwError] = useState('')
  const [pwSuccess, setPwSuccess] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [settings, setSettings] = useState({
    site_name: 'TrenchMonkeyz AI Dashboard',
    theme: 'dark',
    email_notifications: true,
    task_updates: true,
    agent_status_alerts: true,
    message_retention_days: 90,
    status_poll_interval: 30
  })

  const handlePasswordReset = async (e) => {
    e.preventDefault()
    setPwError('')
    setPwSuccess('')

    if (pwData.newPassword !== pwData.confirmPassword) {
      setPwError('New passwords do not match')
      return
    }
    if (pwData.newPassword.length < 6) {
      setPwError('Password must be at least 6 characters')
      return
    }

    setPwSaving(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_BASE}/auth/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: pwData.currentPassword,
          newPassword: pwData.newPassword
        })
      })
      const data = await res.json()
      if (!res.ok) {
        setPwError(data.error || 'Failed to update password')
      } else {
        setPwSuccess('Password updated successfully')
        setPwData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      }
    } catch (err) {
      setPwError('Network error - could not update password')
    }
    setPwSaving(false)
  }

  const handleSave = (e) => {
    e.preventDefault()
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p className="settings-subtitle">Configure your dashboard preferences</p>
      </div>

      <div className="settings-grid">
        {/* Profile */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h2>Profile</h2>
          </div>
          <div className="settings-card-body">
            <div className="profile-info">
              <div className="profile-avatar">{user?.name?.charAt(0) || 'M'}</div>
              <div>
                <div className="profile-name">{user?.name || 'Mark'}</div>
                <div className="profile-email">{user?.email || 'mark@trenchmonkeyz.com'}</div>
                <div className="profile-role">{user?.role || 'Admin'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* General Settings */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h2>General</h2>
          </div>
          <form className="settings-card-body" onSubmit={handleSave}>
            <div className="setting-item">
              <label>Site Name</label>
              <input
                type="text"
                value={settings.site_name}
                onChange={(e) => setSettings(prev => ({ ...prev, site_name: e.target.value }))}
              />
            </div>
            <div className="setting-item">
              <label>Theme</label>
              <select
                value={settings.theme}
                onChange={(e) => setSettings(prev => ({ ...prev, theme: e.target.value }))}
              >
                <option value="dark">Dark (Default)</option>
                <option value="light">Light</option>
                <option value="system">System</option>
              </select>
            </div>
            <div className="setting-item">
              <label>Message Retention (days)</label>
              <input
                type="number"
                value={settings.message_retention_days}
                onChange={(e) => setSettings(prev => ({ ...prev, message_retention_days: parseInt(e.target.value) }))}
                min={1}
                max={365}
              />
            </div>
            <div className="setting-item">
              <label>Status Poll Interval (seconds)</label>
              <input
                type="number"
                value={settings.status_poll_interval}
                onChange={(e) => setSettings(prev => ({ ...prev, status_poll_interval: parseInt(e.target.value) }))}
                min={5}
                max={300}
              />
            </div>
            <button type="submit" className="btn btn-primary">
              {saved ? '✓ Saved!' : <><FiSave /> Save Settings</>}
            </button>
          </form>
        </div>

        {/* Notifications */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h2>Notifications</h2>
          </div>
          <div className="settings-card-body">
            <div className="setting-toggle">
              <div>
                <div className="toggle-label">Email Notifications</div>
                <div className="toggle-desc">Receive email updates for important events</div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.email_notifications}
                  onChange={(e) => setSettings(prev => ({ ...prev, email_notifications: e.target.checked }))}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="setting-toggle">
              <div>
                <div className="toggle-label">Task Updates</div>
                <div className="toggle-desc">Get notified when task status changes</div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.task_updates}
                  onChange={(e) => setSettings(prev => ({ ...prev, task_updates: e.target.checked }))}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="setting-toggle">
              <div>
                <div className="toggle-label">Agent Status Alerts</div>
                <div className="toggle-desc">Alert when agents go offline or come online</div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.agent_status_alerts}
                  onChange={(e) => setSettings(prev => ({ ...prev, agent_status_alerts: e.target.checked }))}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* About */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h2>About</h2>
          </div>
          <div className="settings-card-body about-info">
            <div className="about-item">
              <FiInfo />
              <div>
                <div className="about-label">Version</div>
                <div className="about-value">1.0.0</div>
              </div>
            </div>
            <div className="about-item">
              <FiRefreshCw />
              <div>
                <div className="about-label">Agent Network</div>
                <div className="about-value">18 Agents Online</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings
